// frontend/src/hooks/useRegimeIntel.js
import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchRegimeIntel } from '../services/api';

const DEBUG = process.env.REACT_APP_DEBUG === 'true';

/**
 * Fetches regime intelligence for the given candles. The backend fit is
 * expensive (~2s) but cached per dataset identity, so this hook's job is to
 * avoid redundant requests: a data hash skips refetches while the current bar
 * merely ticks, and a debounce coalesces bursts of candle updates.
 */
export const useRegimeIntel = (candleData, config = {}) => {
  const [data, setData] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);

  const debounceRef = useRef(null);
  const lastHashRef = useRef(null);

  const createDataHash = useCallback((candles) => {
    if (!candles || candles.length === 0) return '';
    const latest = candles[candles.length - 1];
    return `${config.symbol}-${config.timeframe}-${candles.length}-${latest.time}`;
  }, [config.symbol, config.timeframe]);

  const calculate = useCallback(async () => {
    if (config.enabled === false) return; // gated (e.g. logged-out): skip the fit
    if (!candleData || candleData.length === 0) return;

    const hash = createDataHash(candleData);
    if (hash === lastHashRef.current && data) return;

    setIsCalculating(true);
    try {
      const result = await fetchRegimeIntel({
        candles: candleData,
        symbol: config.symbol || 'UNKNOWN',
        timeframe: config.timeframe || '1d',
      });
      lastHashRef.current = hash;
      setData(result);
      setLastUpdate(new Date());
    } catch (error) {
      if (DEBUG) {
        console.warn('Regime intelligence fetch failed:', error);
      }
    } finally {
      setIsCalculating(false);
    }
  }, [candleData, config.symbol, config.timeframe, config.enabled, createDataHash, data]);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(calculate, config.debounceMs || 1000);
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [calculate, config.debounceMs]);

  const refresh = useCallback(() => {
    lastHashRef.current = null;
    calculate();
  }, [calculate]);

  return { data, isCalculating, lastUpdate, refresh };
};

export default useRegimeIntel;
